import { ChangeEvent, MouseEvent, forwardRef } from "react";
import { GenericComponentProps, makeStyles } from "../../../utils/genericComponentProps";
import { TooltipWrapper } from "../../deprecated/DeprecatedTooltip/TooltipWrapper";
import { Icon } from "../../deprecated/Icon/Icon";
import { TextSize } from "../../deprecated/Text/Text";
import TypographyText from "../TypographyText/TypographyText";
import styles from "./Checkbox.module.css";

export type CheckboxProps = GenericComponentProps & {
  description?: string;
  value: boolean | undefined;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  circular?: boolean;
  boldCheckedDescription?: boolean;

  textSize?: TextSize;

  "data-cy"?: string;
  tooltip?: string;
};

// The size of the checkbox is determined by the text size.
const checkboxSizes: Record<TextSize, number> = {
  sm: 16,
  md: 21,
  lg: 21,
  xl: 21,
  xxl: 21,
};

export const Checkbox = forwardRef((props: CheckboxProps, ref: any) => {
  const className = props.className ?? "";
  const style = makeStyles(props);

  const checkboxSize = checkboxSizes[props.textSize ?? "md"];

  const onChange = (e: ChangeEvent<HTMLInputElement> | MouseEvent<HTMLElement> | undefined = undefined) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!props.disabled && props.onChange) {
      props.onChange(!props.value);
    }
  };

  const content = (
    <button
      data-cy={props["data-cy"]}
      onClick={onChange}
      style={style}
      className={`${styles.checkboxContainer} ${className} ${props.circular ? styles.circular : ""}`}
    >
      <input ref={ref} type="checkbox" hidden checked={props.value} onChange={onChange} disabled={props.disabled} />
      <div
        className={`${styles.checkmark} ${props.disabled ? styles.disabled : ""} ${props.value ? styles.checked : ""}`}
        style={{
          width: `${checkboxSize}px`,
          height: `${checkboxSize}px`,
        }}
      >
        {props.value && (
          <Icon
            size={props.circular ? checkboxSize / 2 : checkboxSize}
            className={styles.check_icon}
            name={props.circular ? "circle" : "check"}
            color={!props.disabled ? "white" : "var(--gray-400)"}
          />
        )}
      </div>
      {props.description && (
        <TypographyText
          weight={props.boldCheckedDescription && props.value ? "bold" : "medium"}
          className={styles.description}
          onClick={onChange}
          size={props.textSize === "xxl" ? "2xl" : (props.textSize ?? "md")}
          css={{ cursor: props.disabled ? "not-allowed" : "pointer" }}
        >
          {props.description}
        </TypographyText>
      )}
    </button>
  );

  if (props.tooltip) {
    return <TooltipWrapper trigger={content} title={props.tooltip} />;
  } else {
    return content;
  }
});
